import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import {
    WHATSAPP_CONVERSATION_ACCESS,
    getWhatsappConversationWindowState,
    WHATSAPP_CONVERSATION_DIRECTION,
    WHATSAPP_CONVERSATION_SPECIAL_PERMISSION,
    WHATSAPP_CONVERSATION_STATUS,
    WHATSAPP_EXTERNAL_HANDOFF_STATUS,
    WHATSAPP_CONVERSATION_STATUS_OPTIONS,
    WhatsappAssignableUser,
    WhatsappConversationMessage,
    WhatsappConversationReplyTemplate,
    WhatsappConversationSummary,
} from 'src/app/models/notificacion/whatsapp_conversation.model';
import { SeguridadService } from 'src/app/services/seguridad.service';
import { WhatsappConversationService } from 'src/app/services/notificacion/whatsapp_conversation.service';
import { normalizeParaguayPhone, toWhatsappWebPhone } from 'src/app/utilities/phone-normalizer';

const WHATSAPP_CONVERSATION_ACTIONS_VARIANT = {
    COMPOSER: 'composer',
    MANAGEMENT: 'management',
} as const;

type WhatsappConversationActionsVariant = (typeof WHATSAPP_CONVERSATION_ACTIONS_VARIANT)[keyof typeof WHATSAPP_CONVERSATION_ACTIONS_VARIANT];

@Component({
    selector: 'app-whatsapp-conversation-actions',
    templateUrl: './whatsapp-conversation-actions.component.html',
    styleUrls: ['./whatsapp-conversation-actions.component.scss']
})
export class WhatsappConversationActionsComponent implements OnChanges {

    @Input() conversation: WhatsappConversationSummary | null = null;
    @Input() assignableUsers: WhatsappAssignableUser[] = [];
    @Input() loadingUsers = false;
    @Input() availableReplyTemplates: WhatsappConversationReplyTemplate[] = [];
    @Input() loadingTemplates = false;
    @Input() messages: WhatsappConversationMessage[] = [];
    @Input() variant: WhatsappConversationActionsVariant = WHATSAPP_CONVERSATION_ACTIONS_VARIANT.MANAGEMENT;
    @Output() updated = new EventEmitter<void>();

    readonly statusOptions = WHATSAPP_CONVERSATION_STATUS_OPTIONS;

    processingTake = false;
    processingAssign = false;
    processingStatus = false;
    processingReply = false;
    processingReactivationTemplate = false;
    processingExternalHandoff = false;
    processingReopen = false;

    readonly assignForm = this.formBuilder.group({
        assigned_user_id: new FormControl<number | null>(null, Validators.required),
    });

    readonly statusForm = this.formBuilder.group({
        status_code: new FormControl<number | null>(null, Validators.required),
    });

    readonly replyForm = this.formBuilder.group({
        body_text: new FormControl<string>(''),
        media_url: new FormControl<string>(''),
        template_id: new FormControl<number | null>(null),
        template_code: new FormControl<string>(''),
        template_language: new FormControl<string>('es'),
    });

    readonly externalHandoffForm = this.formBuilder.group({
        target_phone: new FormControl<string>('', Validators.required),
        responsible_name: new FormControl<string>('', Validators.required),
        summary: new FormControl<string>('', Validators.required),
    });

    externalHandoffDialogVisible = false;

    private readonly seguridadService = new SeguridadService();

    constructor(
        private formBuilder: FormBuilder,
        private messageService: MessageService,
        private service: WhatsappConversationService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['conversation'] && this.conversation) {
            this.assignForm.patchValue({ assigned_user_id: this.conversation.assigned_user_id || null }, { emitEvent: false });
            this.statusForm.patchValue({
                status_code: Number(this.conversation.status_code) === WHATSAPP_CONVERSATION_STATUS.CERRADO
                    ? WHATSAPP_CONVERSATION_STATUS.RESUELTO
                    : this.conversation.status_code || null,
            }, { emitEvent: false });
            this.resetReplyForm();
            this.syncExternalHandoffFormWithConversation();
        }

        if ((changes['messages'] || changes['conversation']) && !this.isCurrentExternalSummaryCustomized()) {
            this.externalHandoffForm.patchValue({
                summary: this.generateExternalHandoffSummary(),
            }, { emitEvent: false });
        }
    }

    get currentUserId(): number {
        return Number(this.seguridadService.getCurrentUser()?.id || 0);
    }

    get canTakeConversation(): boolean {
        if (!this.conversation) {
            return false;
        }

        return !this.conversation.assigned_user_id || Number(this.conversation.assigned_user_id) === this.currentUserId;
    }

    get isConversationAssigned(): boolean {
        return Number(this.conversation?.assigned_user_id || 0) > 0;
    }

    get isAssignedToCurrentUser(): boolean {
        if (!this.conversation) {
            return false;
        }

        return Number(this.conversation.assigned_user_id || 0) === this.currentUserId;
    }

    get canReplyToConversation(): boolean {
        return !!this.conversation && this.canEditInbox && this.isAssignedToCurrentUser;
    }

    get canManageAssignment(): boolean {
        return this.canEditInbox
            && (
                this.seguridadService.esSuperusuario()
            || this.seguridadService.tienePermisoEspecialSync(
                WHATSAPP_CONVERSATION_SPECIAL_PERMISSION.MANAGE_ASSIGNMENT.codigo,
            )
        );
    }

    get canEditInbox(): boolean {
        return this.seguridadService.puedeEditarSync(
            WHATSAPP_CONVERSATION_ACCESS.CODIGO_MODULO,
            WHATSAPP_CONVERSATION_ACCESS.CODIGO_REGISTRO,
        );
    }

    get showTakeConversationAction(): boolean {
        return !!this.conversation && this.canEditInbox && !this.isConversationAssigned && this.canTakeConversation;
    }

    get windowState() {
        return getWhatsappConversationWindowState(this.conversation);
    }

    get requiresTemplateReply(): boolean {
        return this.windowState.requiresTemplateReply;
    }

    get selectedTemplate(): WhatsappConversationReplyTemplate | null {
        const templateId = Number(this.replyForm.value.template_id || 0);
        return this.availableReplyTemplates.find((template) => template.id === templateId) || null;
    }

    get canReopenConversation(): boolean {
        if (!this.conversation || !this.canEditInbox) {
            return false;
        }

        const statusCode = Number(this.conversation.status_code);

        return (statusCode === WHATSAPP_CONVERSATION_STATUS.RESUELTO || statusCode === WHATSAPP_CONVERSATION_STATUS.CERRADO)
            || Number(this.conversation.external_handoff_status_code) === WHATSAPP_EXTERNAL_HANDOFF_STATUS.DERIVADA_FUERA;
    }

    get replyButtonLabel(): string {
        return 'Enviar';
    }

    get replyHelperText(): string {
        if (this.requiresTemplateReply) {
            return 'Esta conversación venció por política de WhatsApp. Podés enviar un mensaje para retomarla.';
        }

        return 'Escribí la respuesta y enviála sin salir del chat.';
    }

    get composerBlockedTitle(): string {
        if (!this.isConversationAssigned) {
            return 'Tomá o asigná la conversación para responder';
        }

        if (!this.canEditInbox) {
            return 'Tu grupo puede ver el inbox, pero no editarlo';
        }

        return 'La respuesta está bloqueada para este chat';
    }

    get composerBlockedDescription(): string {
        if (!this.canEditInbox) {
            return 'Necesitás permiso de edición sobre el registro Inbox WhatsApp para tomar, responder o cambiar el estado.';
        }

        if (!this.isConversationAssigned) {
            return this.canManageAssignment
                ? 'Primero dejala a tu nombre o asignala desde gestión. Recién ahí se habilita el composer.'
                : 'La conversación debe quedar asignada a tu usuario antes de responder.';
        }

        return this.canManageAssignment
            ? `Esta conversación está asignada a ${this.conversation?.assigned_user_name || 'otro usuario'}. Si necesitás responder, reasignala desde gestión.`
            : `Esta conversación está asignada a ${this.conversation?.assigned_user_name || 'otro usuario'}. Solo la persona asignada puede responder.`;
    }

    get assignmentBannerClass(): string {
        return this.isConversationAssigned ? 'banner-info' : 'banner-warning';
    }

    get assignmentBannerTitle(): string {
        if (!this.isConversationAssigned) {
            return 'Conversación sin asignar';
        }

        return `Asignada a ${this.conversation?.assigned_user_name || 'usuario actual'}`;
    }

    get assignmentBannerDetail(): string {
        if (!this.isConversationAssigned) {
            return 'La próxima acción es tomarla o asignarla. Hasta entonces no se puede responder.';
        }

        return this.canManageAssignment
            ? 'Podés reasignarla desde acá si el caso cambia de dueño.'
            : 'La asignación queda visible, pero tu grupo de acceso no puede modificarla.';
    }

    get showComposerSection(): boolean {
        return this.variant === WHATSAPP_CONVERSATION_ACTIONS_VARIANT.COMPOSER;
    }

    get showManagementSection(): boolean {
        return this.variant === WHATSAPP_CONVERSATION_ACTIONS_VARIANT.MANAGEMENT;
    }

    openExternalHandoffDialog(): void {
        this.syncExternalHandoffFormDefaults();
        this.externalHandoffDialogVisible = true;
    }

    closeExternalHandoffDialog(): void {
        this.externalHandoffDialogVisible = false;
        this.syncExternalHandoffFormWithConversation();
    }

    regenerateExternalHandoffSummary(): void {
        this.externalHandoffForm.patchValue({
            summary: this.generateExternalHandoffSummary(),
        }, { emitEvent: false });
    }

    normalizeExternalHandoffTargetPhone(): void {
        const currentValue = String(this.externalHandoffForm.value.target_phone || '').trim();
        const normalizedPhone = normalizeParaguayPhone(currentValue);

        if (!normalizedPhone || normalizedPhone === currentValue) {
            return;
        }

        this.externalHandoffForm.patchValue({
            target_phone: normalizedPhone,
        }, { emitEvent: false });
    }

    onTemplateChange(): void {
        const template = this.selectedTemplate;
        this.replyForm.patchValue({
            template_code: this.codigoDePlantilla(template),
            template_language: template?.language || 'es',
            body_text: template?.mensaje || '',
            media_url: template?.url_imagen || '',
        }, { emitEvent: false });
    }

    /**
     * Código con el que se identifica la plantilla al enviarla.
     *
     * En Meta la identidad es el nombre técnico (`meta_template_name`), no el
     * `content_sid` opaco de Twilio. Se conserva el respaldo al `content_sid`
     * mientras queden plantillas sin recrear, y al nombre visible como último
     * recurso.
     */
    private codigoDePlantilla(template: WhatsappConversationReplyTemplate | null): string {
        return template?.meta_template_name
            || template?.content_sid
            || template?.nombre
            || '';
    }

    async takeConversation(): Promise<void> {
        if (!this.conversation || !this.showTakeConversationAction) {
            return;
        }

        if (!this.canEditInbox) {
            this.messageService.add({ severity: 'warn', summary: 'Edición restringida', detail: 'Tu grupo de acceso no puede tomar conversaciones en Inbox WhatsApp.' });
            return;
        }

        try {
            this.processingTake = true;
            const response = await this.service.takeConversation(this.conversation.id, this.conversation.id_empresa);
            if (!response.ok) {
                this.messageService.add({ severity: 'error', summary: response.msg, detail: response.resp });
                return;
            }

            this.messageService.add({ severity: 'success', summary: 'Conversación tomada', detail: 'La conversación quedó a tu nombre.' });
            this.updated.emit();
        } finally {
            this.processingTake = false;
        }
    }

    async assignConversation(): Promise<void> {
        if (!this.canManageAssignment) {
            this.messageService.add({ severity: 'warn', summary: 'Asignación restringida', detail: 'Tu grupo de acceso no tiene permiso para reasignar conversaciones.' });
            return;
        }

        if (!this.conversation || this.assignForm.invalid) {
            this.assignForm.markAllAsTouched();
            return;
        }

        try {
            this.processingAssign = true;
            const response = await this.service.assignConversation(
                this.conversation.id,
                Number(this.assignForm.value.assigned_user_id),
                this.conversation.id_empresa,
            );

            if (!response.ok) {
                this.messageService.add({ severity: 'error', summary: response.msg, detail: response.resp });
                return;
            }

            this.messageService.add({ severity: 'success', summary: 'Conversación asignada', detail: 'La reasignación se guardó correctamente.' });
            this.updated.emit();
        } finally {
            this.processingAssign = false;
        }
    }

    async changeStatus(): Promise<void> {
        if (!this.canEditInbox) {
            this.messageService.add({ severity: 'warn', summary: 'Edición restringida', detail: 'Tu grupo de acceso no puede cambiar el estado de Inbox WhatsApp.' });
            return;
        }

        if (!this.conversation || this.statusForm.invalid) {
            this.statusForm.markAllAsTouched();
            return;
        }

        try {
            this.processingStatus = true;
            const response = await this.service.updateConversationStatus(
                this.conversation.id,
                Number(this.statusForm.value.status_code),
                this.conversation.id_empresa,
            );

            if (!response.ok) {
                this.messageService.add({ severity: 'error', summary: response.msg, detail: response.resp });
                return;
            }

            this.messageService.add({ severity: 'success', summary: 'Estado actualizado', detail: 'Se actualizó el estado operativo de la conversación.' });
            this.updated.emit();
        } finally {
            this.processingStatus = false;
        }
    }

    async sendReply(): Promise<void> {
        if (!this.conversation) {
            return;
        }

        if (!this.canEditInbox) {
            this.messageService.add({ severity: 'warn', summary: 'Edición restringida', detail: 'Tu grupo de acceso no puede responder conversaciones de Inbox WhatsApp.' });
            return;
        }

        if (!this.canReplyToConversation) {
            this.messageService.add({ severity: 'warn', summary: 'Respuesta bloqueada', detail: 'Tomá o asigná la conversación antes de responder.' });
            return;
        }

        const bodyText = String(this.replyForm.value.body_text || '').trim();
        const mediaUrl = String(this.replyForm.value.media_url || '').trim();
        const templateId = Number(this.replyForm.value.template_id || 0);

        if (this.requiresTemplateReply && !templateId) {
            this.messageService.add({ severity: 'warn', summary: 'Mensaje pendiente', detail: 'Elegí un mensaje aprobado antes de enviar.' });
            return;
        }

        if (!bodyText && !mediaUrl) {
            this.messageService.add({ severity: 'warn', summary: 'Mensaje incompleto', detail: 'Escribí un mensaje antes de enviar.' });
            return;
        }

        try {
            this.processingReply = true;
            const response = await this.service.replyConversation(this.conversation.id, {
                body_text: bodyText,
                media_url: mediaUrl,
                template_id: templateId || null,
                template_code: this.codigoDePlantilla(this.selectedTemplate),
                template_language: this.selectedTemplate?.language || 'es',
            }, this.conversation.id_empresa);

            if (!response.ok) {
                this.messageService.add({ severity: 'error', summary: response.msg, detail: response.resp });
                return;
            }

            const providerSuccess = response.resp?.provider?.success !== false;
            this.messageService.add({
                severity: providerSuccess ? 'success' : 'warn',
                summary: providerSuccess
                    ? (this.requiresTemplateReply ? 'Template enviado' : 'Reply enviado')
                    : (this.requiresTemplateReply ? 'Template registrado con advertencia' : 'Reply registrado con advertencia'),
                detail: providerSuccess
                    ? 'La respuesta se registró y Meta confirmó el envío.'
                    : response.resp?.provider?.error || 'Meta no confirmó el envío, pero el reply quedó auditado.',
            });

            this.resetReplyForm();
            this.updated.emit();
        } finally {
            this.processingReply = false;
        }
    }

    async sendReactivationTemplate(): Promise<void> {
        if (!this.conversation) {
            return;
        }

        if (!this.canEditInbox) {
            this.messageService.add({ severity: 'warn', summary: 'Edición restringida', detail: 'Tu grupo de acceso no puede responder conversaciones de Inbox WhatsApp.' });
            return;
        }

        if (!this.canReplyToConversation) {
            this.messageService.add({ severity: 'warn', summary: 'Respuesta bloqueada', detail: 'Tomá o asigná la conversación antes de responder.' });
            return;
        }

        try {
            this.processingReactivationTemplate = true;
            const response = await this.service.sendReactivationTemplate(this.conversation.id, this.conversation.id_empresa);
            if (!response.ok) {
                this.messageService.add({ severity: 'warn', summary: response.msg, detail: response.resp });
                return;
            }

            const providerSuccess = response.resp?.provider?.success !== false;
            this.messageService.add({
                severity: providerSuccess ? 'success' : 'warn',
                summary: providerSuccess ? 'Mensaje para retomar enviado' : 'Mensaje registrado con advertencia',
                detail: providerSuccess
                    ? 'Se envió el template de reactivación al cliente.'
                    : response.resp?.provider?.error || 'Meta no confirmó el envío, pero el mensaje quedó auditado.',
            });
            this.updated.emit();
        } finally {
            this.processingReactivationTemplate = false;
        }
    }

    async saveExternalHandoff(): Promise<void> {
        if (!this.canEditInbox) {
            this.messageService.add({ severity: 'warn', summary: 'Edición restringida', detail: 'Tu grupo de acceso no puede derivar conversaciones desde Inbox WhatsApp.' });
            return;
        }

        if (!this.conversation || this.externalHandoffForm.invalid) {
            this.externalHandoffForm.markAllAsTouched();
            this.messageService.add({ severity: 'warn', summary: 'Datos incompletos', detail: 'Completá número destino, responsable y resumen antes de abrir WhatsApp Web.' });
            return;
        }

        const normalizedTargetPhone = normalizeParaguayPhone(this.externalHandoffForm.value.target_phone || '');
        if (!normalizedTargetPhone) {
            this.messageService.add({ severity: 'warn', summary: 'Destino inválido', detail: 'Ingresá un número de Paraguay válido. Podés pegarlo con 0, 595, +595, espacios o guiones; Finexus lo acomoda solo.' });
            this.externalHandoffForm.controls.target_phone.markAsTouched();
            return;
        }

        this.externalHandoffForm.patchValue({
            target_phone: normalizedTargetPhone,
        }, { emitEvent: false });

        const popup = window.open('', '_blank');

        try {
            this.processingExternalHandoff = true;
            const response = await this.service.externalHandoffConversation(this.conversation.id, {
                target_phone: normalizedTargetPhone,
                responsible_name: String(this.externalHandoffForm.value.responsible_name || '').trim(),
                summary: String(this.externalHandoffForm.value.summary || '').trim(),
            }, this.conversation.id_empresa);

            if (!response.ok) {
                popup?.close();
                this.messageService.add({ severity: 'error', summary: response.msg, detail: response.resp });
                return;
            }

            const targetPhone = this.extractTargetPhoneForWhatsapp(response.resp) || normalizedTargetPhone;
            const whatsappMessage = this.buildExternalWhatsappMessage();
            const whatsappUrl = this.buildWhatsappWebUrl(targetPhone, whatsappMessage);

            if (!whatsappUrl) {
                popup?.close();
                this.messageService.add({ severity: 'warn', summary: 'Destino inválido', detail: 'La derivación quedó auditada, pero no se pudo abrir WhatsApp Web porque el número destino no es válido.' });
                this.externalHandoffDialogVisible = false;
                this.updated.emit();
                return;
            }

            if (popup && !popup.closed) {
                popup.opener = null;
                popup.location.replace(whatsappUrl);
            } else {
                window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
            }

            this.messageService.add({ severity: 'success', summary: 'Derivación externa registrada', detail: 'Se auditó la derivación y se abrió WhatsApp Web con el resumen listo para enviar.' });
            this.externalHandoffDialogVisible = false;
            this.updated.emit();
        } catch (error) {
            popup?.close();
            throw error;
        } finally {
            this.processingExternalHandoff = false;
        }
    }

    async reopenConversation(): Promise<void> {
        if (!this.conversation) {
            return;
        }

        if (!this.canEditInbox) {
            this.messageService.add({ severity: 'warn', summary: 'Edición restringida', detail: 'Tu grupo de acceso no puede reabrir conversaciones de Inbox WhatsApp.' });
            return;
        }

        try {
            this.processingReopen = true;
            const response = await this.service.reopenConversation(this.conversation.id, this.conversation.id_empresa);
            if (!response.ok) {
                this.messageService.add({ severity: 'error', summary: response.msg, detail: response.resp });
                return;
            }

            this.messageService.add({ severity: 'success', summary: 'Conversación reabierta', detail: 'La conversación volvió a estado operativo y puede retomarse desde inbox.' });
            this.updated.emit();
        } finally {
            this.processingReopen = false;
        }
    }

    private resetReplyForm(): void {
        this.replyForm.reset({
            body_text: '',
            media_url: '',
            template_id: null,
            template_code: '',
            template_language: 'es',
        }, { emitEvent: false });
    }

    private getDefaultResponsibleName(): string {
        const currentUserName = String(this.seguridadService.getCurrentUser()?.nombre || '').trim();
        return currentUserName || String(this.conversation?.assigned_user_name || '').trim();
    }

    private syncExternalHandoffFormDefaults(): void {
        const currentSummary = String(this.externalHandoffForm.value.summary || '').trim();
        const currentResponsibleName = String(this.externalHandoffForm.value.responsible_name || '').trim();

        this.externalHandoffForm.patchValue({
            responsible_name: currentResponsibleName || this.getDefaultResponsibleName(),
            summary: currentSummary || this.generateExternalHandoffSummary(),
        }, { emitEvent: false });
    }

    private syncExternalHandoffFormWithConversation(): void {
        if (!this.conversation) {
            return;
        }

        if (this.conversation.external_handoff_status_code === WHATSAPP_EXTERNAL_HANDOFF_STATUS.DERIVADA_FUERA) {
            this.externalHandoffForm.patchValue({
                target_phone: normalizeParaguayPhone(this.conversation.external_handoff_target_phone) || this.conversation.external_handoff_target_phone || '',
                responsible_name: this.conversation.external_handoff_responsible_name || '',
                summary: this.conversation.external_handoff_summary || '',
            }, { emitEvent: false });
            return;
        }

        this.externalHandoffForm.reset({
            target_phone: '',
            responsible_name: this.getDefaultResponsibleName(),
            summary: this.generateExternalHandoffSummary(),
        }, { emitEvent: false });
    }

    private isCurrentExternalSummaryCustomized(): boolean {
        const currentSummary = String(this.externalHandoffForm.value.summary || '').trim();
        if (!currentSummary) {
            return false;
        }

        return currentSummary !== this.generateExternalHandoffSummary();
    }

    private generateExternalHandoffSummary(): string {
        if (!this.conversation) {
            return '';
        }

        const title = `Cliente: ${this.conversation.nombre || 'Sin nombre'} (${this.conversation.customer_phone_normalized || this.conversation.customer_phone})`;
        const assignedTo = this.conversation.assigned_user_name ? `Asignado a: ${this.conversation.assigned_user_name}` : 'Asignado a: Sin asignar';
        const recentMessages = this.messages
            .slice(-6)
            .map((message) => {
                const actor = message.actor_user_name || (
                    Number(message.direction_code) === WHATSAPP_CONVERSATION_DIRECTION.OUTBOUND
                        ? 'Asesor'
                        : 'Cliente'
                );
                const text = String(message.body_text || message.media_url || 'Mensaje sin texto').replace(/\s+/g, ' ').trim();
                return `- ${actor}: ${text.slice(0, 180)}`;
            });

        return [
            title,
            assignedTo,
            'Resumen de conversación:',
            ...(recentMessages.length > 0 ? recentMessages : ['- Sin mensajes visibles todavía.']),
        ].join('\n');
    }

    private buildExternalWhatsappMessage(): string {
        const responsibleName = String(this.externalHandoffForm.value.responsible_name || '').trim();
        const summary = String(this.externalHandoffForm.value.summary || '').trim();

        return [
            'Derivación externa Finexus',
            responsibleName ? `Responsable: ${responsibleName}` : null,
            '',
            summary,
        ].filter(Boolean).join('\n');
    }

    private extractTargetPhoneForWhatsapp(responseBody: unknown): string {
        const response = (responseBody || {}) as Record<string, unknown>;
        return String(
            response['external_handoff_target_phone']
            || (response['conversation'] as Record<string, unknown> | undefined)?.['external_handoff_target_phone']
            || ''
        ).trim();
    }

    private buildWhatsappWebUrl(targetPhone: string, message: string): string | null {
        const normalizedPhone = toWhatsappWebPhone(targetPhone);
        if (!normalizedPhone) {
            return null;
        }

        return `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(message)}`;
    }
}
